import { useId, useState } from 'react';
import { SendOutlined } from '@ant-design/icons';

import { Button, Field, Select, Textarea } from '../../../../components/ui';
import { useLoi } from '../../../../hooks/useLoi';
import { useT } from '../../../../i18n';
import { useToast } from '../../../../lib/toast';
import { LoiTrongHop } from '../../../tenant/components/phan-form';
import { useGiaoTaskAgent } from '../../hooks/useAgent';
import { kiemYeuCau, MAU_YEU_CAU, PHAM_VI_NGAY, YEU_CAU_TOI_DA } from '../../nghiep-vu/agent';

/**
 * Giao việc cho agent (`AgentTaskForm` của native): mẫu yêu cầu nhanh, chọn agent, phạm vi ngày và nội dung. Giao xong
 * thì xoá ô yêu cầu, giữ agent và phạm vi để giao tiếp việc khác.
 */
export function FormGiaoViec({ agents, khoa }: { agents: string[]; khoa: boolean }) {
  const { t } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const formId = useId();
  const giao = useGiaoTaskAgent();
  const [model, setModel] = useState('');
  const [phamVi, setPhamVi] = useState<number>(PHAM_VI_NGAY[0]);
  const [yeuCau, setYeuCau] = useState('');
  const [loi, setLoi] = useState<'batBuoc' | 'quaDai' | null>(null);
  // Agent đã chọn không còn trong danh sách Gateway báo thì về agent đầu.
  const agent = agents.includes(model) ? model : (agents[0] ?? '');

  const gui = () => {
    const sai = kiemYeuCau(yeuCau);
    setLoi(sai);
    if (sai || giao.isPending) return;
    giao.mutate(
      { prompt: yeuCau.trim(), scopeDays: phamVi, model: agent },
      {
        onSuccess: () => {
          toast.success(t('rpaAgent.daGiao'));
          setYeuCau('');
        },
      },
    );
  };

  return (
    <form
      id={formId}
      noValidate
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        gui();
      }}
    >
      {khoa && <p className="text-xs text-warning-700">{t('rpaAgent.form.chuaKetNoi')}</p>}
      <div>
        <p className="mb-2 text-xs text-content-muted">{t('rpaAgent.form.mauNhanh')}</p>
        <div className="flex flex-wrap gap-2">
          {MAU_YEU_CAU.map((m) => (
            <Button
              key={m}
              variant="outline"
              size="sm"
              disabled={khoa}
              onClick={() => {
                setYeuCau(t(`rpaAgent.mauNoiDung.${m}`));
                setLoi(null);
                giao.reset();
              }}
            >
              {t(`rpaAgent.mauNhan.${m}`)}
            </Button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1">
        <Field label={t('rpaAgent.form.agent')} required>
          {(p) => (
            <Select<string>
              id={p.id}
              className="w-full"
              disabled={khoa}
              value={agent}
              onChange={setModel}
              options={agents.map((a) => ({ value: a, label: a }))}
            />
          )}
        </Field>
        <Field label={t('rpaAgent.form.phamVi')} required hint={t('rpaAgent.form.phamViGoiY')}>
          {(p) => (
            <Select<number>
              id={p.id}
              aria-describedby={p['aria-describedby']}
              className="w-full"
              disabled={khoa}
              value={phamVi}
              onChange={setPhamVi}
              options={PHAM_VI_NGAY.map((d) => ({ value: d, label: t('rpaAgent.phamViNgay', { so: d }) }))}
            />
          )}
        </Field>
      </div>
      <Field
        label={t('rpaAgent.form.yeuCau')}
        required
        hint={t('rpaAgent.form.yeuCauGoiY')}
        error={
          loi === 'batBuoc' ? t('rpaAgent.form.batBuoc') : loi === 'quaDai' ? t('rpaAgent.form.quaDai', { max: YEU_CAU_TOI_DA }) : undefined
        }
      >
        {(p) => (
          <Textarea
            {...p}
            rows={5}
            maxLength={YEU_CAU_TOI_DA}
            showCount
            disabled={khoa}
            placeholder={t('rpaAgent.form.yeuCauMau')}
            value={yeuCau}
            onChange={(e) => {
              setYeuCau(e.target.value);
              setLoi(null);
              giao.reset();
            }}
          />
        )}
      </Field>
      {giao.error && <LoiTrongHop>{dichLoi(giao.error)}</LoiTrongHop>}
      <div className="flex justify-end">
        <Button variant="primary" size="md" htmlType="submit" icon={<SendOutlined />} loading={giao.isPending} disabled={khoa}>
          {giao.isPending ? t('rpaAgent.form.dangGui') : t('rpaAgent.form.gui')}
        </Button>
      </div>
    </form>
  );
}
